/* Copyright (c) 2018-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { Logger, LoggingService } from '../logging/logging.service';
import { AppWindowNames } from '../window/window.config';
import { DbConfigService } from './db-config.service';
import { DbDataStore } from './models/db-data-store.class';
import { DBHandler } from './models/db-handler';
import { DbStoreDefinitionMap } from './models/db.models';

@Injectable({
    providedIn: 'root'
})
export class DbService {
    private dbhandlerInstance: DBHandler;
    private readonly globalStoreDefMap: DbStoreDefinitionMap;
    private readonly userStoreDefMap: DbStoreDefinitionMap;
    private readonly dbName: AppWindowNames;
    private logger: Logger;

    constructor(dbConfigService: DbConfigService, loggingService: LoggingService) {
        this.logger = loggingService.getLogger('crimson/DBService');
        this.globalStoreDefMap = dbConfigService.globalStores;
        this.userStoreDefMap = dbConfigService.userStores;
        this.dbName = dbConfigService.appDbName;
        this.dbhandlerInstance = new DBHandler(this.dbName, this.globalStoreDefMap, this.userStoreDefMap);
    }

    get dbHandler(): DBHandler {
        return this.dbhandlerInstance;
    }

    getGlobalStore(storeName: string): DbDataStore {
        return this.dbhandlerInstance.getGlobalStore(storeName);
    }

    getUserStore(userId: string, storeName: string): DbDataStore {
        return this.dbhandlerInstance.getUserStore(userId, storeName);
    }

    getStore(storeName: string): DbDataStore {
        return this.dbhandlerInstance.getStore(storeName);
    }

    private checkKeysOfExisitngDbStore(storeName: string): Observable<number> {
        return this.dbhandlerInstance.checkIfStoreExists(storeName).pipe(
            take(1),
            mergeMap(exists => {
                if (!exists) {
                    return of(0);
                }
                return this.getGlobalStore(storeName).length();
            })
        );
    }

    deleteOldKeys(dataStore: string): Observable<boolean> {
        return this.checkKeysOfExisitngDbStore(dataStore).pipe(
            mergeMap((length: number) => {
                if (!length) {
                    return of(null);
                }
                // Clearing will delete all the keys under the store, but the store name will still exist as parent key
                return this.getGlobalStore(dataStore).clear();
            }),
            map(result => {
                if (!result) {
                    this.logger.info('No keys in the dB store, skip deletion');
                    return false;
                }
                this.logger.info('Keys in db store deleted');
                return true;
            })
        );
    }
}
